import * as React from 'react';
import { User, Activity, Calendar, MessageSquare, Target, X, Check } from 'lucide-react';
import { getSupabase } from '@/lib/supabase';
import { useGlobalState } from '../../components/GlobalStateProvider';
import { redeemInvite, signOutOnServer, syncSupabaseSession } from '@/data/authApi';
import { reportSwallowed } from '@/lib/sentry';

export default function Onboarding() {
  const search = new URLSearchParams(window.location.search);
  const inviteIdParam = search.get('invite') || '';
  const inviteToken = search.get('token') || '';
  const inviteEmail = search.get('email') || '';
  // The intake/onboarding flow needs the live GlobalState (saveIntakeAnswer,
  // submitIntake, finalizeIntake, setUserType, etc.). Reading
  // (window as any).gsRef worked in DEV only — that property is gated behind
  // import.meta.env.DEV in GlobalStateProvider, so it is undefined in prod and
  // every gs.* call here threw at runtime. Use the context like every other
  // consumer in the app.
  const gs: any = useGlobalState();
  const [resolvedInviteId, setResolvedInviteId] = React.useState(inviteIdParam);
  const [resolvingInvite, setResolvingInvite] = React.useState(!inviteIdParam && !!inviteToken && !!inviteEmail);

  const [packet, setPacket] = React.useState<any>(null);
  const [formFlags, setFormFlags] = React.useState<Record<string, any>>({});
  const [trackerDefs, setTrackerDefs] = React.useState<any[]>([]);
  const [customDefs, setCustomDefs] = React.useState<Array<{ key: string; prompt: string; required?: boolean }>>([]);
  const [customAnswers, setCustomAnswers] = React.useState<Record<string, any>>({});
  const [personal, setPersonal] = React.useState<Record<string, any>>({});
  const [experienceVals, setExperienceVals] = React.useState<Record<string, any>>({});
  const [loading, setLoading] = React.useState(true);
  const [_saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [_currentView, setCurrentView] = React.useState<'intake' | 'preview'>('intake');
  const [_editingTracker, _setEditingTracker] = React.useState<string | null>(null);

  const [needsVerification, setNeedsVerification] = React.useState(false);
  const [verificationEmail, setVerificationEmail] = React.useState('');
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [verifyRetrying, setVerifyRetrying] = React.useState(false);
  const [verifyError, setVerifyError] = React.useState<string | null>(null);

  const saveTimer = React.useRef<number | null>(null);
  // TODO(intake-data-loss, found 2026-10-24 during Sec2): one shared timer for EVERY field, so editing
  // field B within `ms` of field A clears A's pending save, and A is never written. Debounce per
  // section:key instead.
  const debounced = (fn: () => Promise<any>, ms = 500) => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      setSaving(true);
      try {
        await fn();
      } catch (e: any) {
        // setTimeout doesn't await its async callback, so a rejection here
        // becomes an unhandled promise rejection — which is exactly what was
        // showing up in Sentry as "Object captured as promise rejection with
        // keys: code, details, hint, message" (bare Supabase error, no
        // stack). Surface a user-visible message instead, and log so Sentry
        // captures the real Error with a stack trace.
        const message = e?.message || 'Failed to save your progress';
        setError(message);
        // eslint-disable-next-line no-console
        console.error('[onboarding] debounced save failed:', e);
      } finally {
        setSaving(false);
      }
    }, ms) as unknown as number;
  };

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldown((c) => {
        if (c <= 1) {
          clearInterval(id);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (inviteIdParam || !inviteToken || !inviteEmail) {
        setResolvingInvite(false);
        return;
      }

      try {
        const sb = getSupabase();
        if (!sb) {
          if (!cancelled) {
            setError('Authentication is not configured. Please ask your coach to resend the invite.');
            setResolvingInvite(false);
          }
          return;
        }

        const {
          data: { user },
        } = await sb.auth.getUser();
        if (!user?.id) {
          if (!cancelled) {
            const returnTo = encodeURIComponent(
              `/onboarding?token=${encodeURIComponent(inviteToken)}&email=${encodeURIComponent(inviteEmail)}`,
            );
            window.location.replace(`/client/auth?returnTo=${returnTo}`);
            setResolvingInvite(false);
          }
          return;
        }

        const redeemed = await redeemInvite({ token: inviteToken, email: inviteEmail, userId: user.id });
        if (!redeemed.ok || !redeemed.inviteId) {
          if (!cancelled) {
            setError(
              !redeemed.ok && redeemed.status === undefined
                ? // Never reached the server — same copy the old network-error catch showed.
                  'Unable to validate invite link. Please try again.'
                : redeemed.message ||
                    'This invite link is invalid or expired. Please ask your coach to send a new invite.',
            );
            setResolvingInvite(false);
          }
          return;
        }

        const inviteId = String(redeemed.inviteId);
        if (cancelled) return;
        setResolvedInviteId(inviteId);
        setResolvingInvite(false);
        window.history.replaceState({}, '', `/onboarding?invite=${encodeURIComponent(inviteId)}`);
      } catch {
        if (!cancelled) {
          setError('Unable to validate invite link. Please try again.');
          setResolvingInvite(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inviteIdParam, inviteToken, inviteEmail]);

  React.useEffect(() => {
    (async () => {
      try {
        if (resolvingInvite) {
          return;
        }

        if (!resolvedInviteId) {
          setPacket(null);
          return;
        }

        // Helper: attempt to sync client-side Supabase session → server cookies
        const trySyncSession = (): Promise<boolean> => syncSupabaseSession(getSupabase());

        // Load (or auto-create) the packet via server-side API to bypass RLS
        let res = await fetch(`/api/onboarding/packet?invite=${encodeURIComponent(resolvedInviteId)}`, {
          credentials: 'include',
        });
        // OBS-11 a-ignore: an unparseable body becomes null, which the !ok / missing-field check right after handles.
        const firstJson = await res.json().catch(() => null);

        // If 401, try syncing client-side session to cookies and retry once
        if (res.status === 401) {
          const synced = await trySyncSession();
          if (synced) {
            res = await fetch(`/api/onboarding/packet?invite=${encodeURIComponent(resolvedInviteId)}`, {
              credentials: 'include',
            });
            // OBS-11 a-ignore: an unparseable body becomes null, which the !ok / missing-field check right after handles.
            const retryJson = await res.json().catch(() => null);
            if (!res.ok || !retryJson?.ok || !retryJson?.packet?.id) {
              const json = retryJson;
              if (res.status === 403 && json?.code === 'EMAIL_NOT_VERIFIED') {
                setNeedsVerification(true);
                setVerificationEmail(json.email || '');
                return;
              }
              if (res.status === 403) {
                setError(
                  json?.message ||
                    'This invite was sent to a different email address. Please sign in with the correct account.',
                );
                return;
              }
              if (res.status === 401) {
                setError('Please sign in to access your onboarding form.');
                return;
              }
            }
            setPacket(retryJson.packet);
            if (retryJson.packet.formFlags) setFormFlags(retryJson.packet.formFlags);
            if (retryJson.packet.trackers) setTrackerDefs(retryJson.packet.trackers);
            if (retryJson.packet.personal) setPersonal(retryJson.packet.personal);
            if (retryJson.packet.experience) setExperienceVals(retryJson.packet.experience);
            if (retryJson.packet.customQuestions) {
              setCustomDefs(
                Object.entries(retryJson.packet.customQuestions).map(([key, val]: any) => ({
                  key,
                  prompt: String(val?.label ?? val?.prompt ?? ''),
                  required: !!val?.required,
                })),
              );
            }
            if (retryJson.packet.customResponses) setCustomAnswers(retryJson.packet.customResponses);
            return;
          }
        }
        const json = firstJson;

        if (!res.ok || !json?.ok || !json?.packet?.id) {
          if (res.status === 403 && json?.code === 'EMAIL_NOT_VERIFIED') {
            setNeedsVerification(true);
            setVerificationEmail(json.email || '');
            return;
          }
          if (res.status === 403) {
            setError(
              json?.message ||
                'This invite was sent to a different email address. Please sign in with the correct account.',
            );
            return;
          }
          if (res.status === 401) {
            setError('Please sign in to access your onboarding form.');
            return;
          }
          // Fallback: try client-side Supabase in case the worker isn't available
          try {
            const { intakeStore } = await import('@/data/storage/intake/adapter');
            const fallback = await (intakeStore as any).getPacketByInvite?.(resolvedInviteId);
            if (fallback?.id) {
              await refreshFromPacket(fallback.id);
              setPacket(fallback);
              return;
            }
          } catch (err) {
            // OBS-11: the UI degrades and carries on, but the failure is reported (once per page load).
            reportSwallowed('Onboarding.packetFallback', err);
          }
          setPacket(null);
          return;
        }

        const pkt = json.packet;
        setPacket(pkt);

        // Hydrate local state from the API response
        if (pkt.formFlags) setFormFlags(pkt.formFlags);
        if (pkt.trackers) setTrackerDefs(pkt.trackers);
        if (pkt.personal) setPersonal(pkt.personal);
        if (pkt.experience) setExperienceVals(pkt.experience);
        if (pkt.customQuestions) {
          setCustomDefs(
            Object.entries(pkt.customQuestions).map(([key, val]: any) => ({
              key,
              prompt: String(val?.label ?? val?.prompt ?? ''),
              required: !!val?.required,
            })),
          );
        }
        if (pkt.customResponses) setCustomAnswers(pkt.customResponses);
      } catch (e: any) {
        setError(e?.message || 'Failed to load onboarding packet');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedInviteId, resolvingInvite]);

  const refreshFromPacket = React.useCallback(async (pid: string) => {
    try {
      const { intakeStore } = await import('@/data/storage/intake/adapter');
      const listAns = (await (intakeStore as any).listAnswersForPacket?.(pid)) || [];
      const bySection: Record<string, Record<string, any>> = {};
      const seenTs: Record<string, Record<string, string>> = {};
      for (const a of listAns) {
        const sec = a?.section || 'misc';
        const key = a?.key;
        const ts = (a as any)?.updated_at || (a as any)?.created_at || '';
        if (!key) continue;
        if (!bySection[sec]) {
          bySection[sec] = {};
          seenTs[sec] = {};
        }
        const prev = seenTs[sec][key] || '';
        if (!prev || ts >= prev) {
          bySection[sec][key] = a.value;
          seenTs[sec][key] = ts;
        }
      }
      const ff = bySection['form_flags'] || {};
      const trackers = (await (intakeStore as any).listTrackerDrafts?.(pid)) || [];
      const cqDefs = Object.entries(bySection['custom_questions'] || {}).map(([key, val]: any) => ({
        key,
        prompt: String(val?.label ?? val?.prompt ?? ''),
        required: !!val?.required,
      }));
      const existingCustomResp = (bySection['custom_responses'] || {}) as Record<string, any>;
      setFormFlags(ff);
      setTrackerDefs(trackers);
      setCustomDefs(cqDefs);
      setCustomAnswers(existingCustomResp || {});
      setPersonal(bySection['personal'] || {});
      setExperienceVals(bySection['experience'] || {});
    } catch (err) {
      // OBS-11: the UI degrades and carries on, but the failure is reported (once per page load).
      reportSwallowed('Onboarding.refreshFromPacket', err);
    }
  }, []);

  const writeAnswer = (section: string, key: string, value: any) => {
    if (!packet?.id) return;
    // Optimistic update - set local state immediately for responsive UI
    if (section === 'personal') setPersonal((p: any) => ({ ...p, [key]: value }));
    if (section === 'experience') setExperienceVals((p: any) => ({ ...p, [key]: value }));
    // Debounced save to DB - DO NOT refresh from packet to avoid overwriting local state mid-typing
    debounced(async () => {
      await gs.saveIntakeAnswer?.(packet.id, section as any, key, value);
      // Removed: await refreshFromPacket(packet.id) - causes input flicker
    });
  };

  const saveCustomAnswer = React.useCallback(
    async (k: string, v: any) => {
      if (!packet?.id) return;
      setCustomAnswers((prev) => ({ ...prev, [k]: v }));
      const currentPacketId = packet.id; // capture to avoid races
      const valueToSave = v;
      debounced(async () => {
        await gs.saveIntakeAnswer?.(currentPacketId, 'custom_responses', k, valueToSave);
      });
    },
    [packet?.id, gs],
  );

  const _addTracker = (t: any) => {
    if (!packet?.id) return;
    debounced(async () => {
      await gs.addIntakeTracker?.(packet.id, t);
      await refreshFromPacket(packet.id);
    });
  };
  const _removeTrackerByTitle = (title: string) => {
    if (!packet?.id) return;
    debounced(async () => {
      await gs.removeIntakeTracker?.(packet.id, title);
      await refreshFromPacket(packet.id);
    });
  };

  // Validation error state for displaying inline messages
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = React.useState(false);

  const onSubmit = async () => {
    if (!packet) return;
    setValidationError(null);
    setError(null);

    // block finish if required custom questions are unanswered
    try {
      const all = await gs.listIntakeAnswers?.(packet.id);
      const defs = (all || []).filter((a: any) => a.section === 'custom_questions');
      const resp: Record<string, string> = Object.fromEntries(
        (all || []).filter((a: any) => a.section === 'custom_responses').map((a: any) => [a.key, a.value]),
      );
      const missing = defs.filter((d: any) => !!d?.value?.required && !String(resp[d.key] || '').trim()).length;
      if (missing > 0) {
        setValidationError('Please answer all required questions.');
        // Focus on error message for screen readers
        setTimeout(() => {
          document.getElementById('validation-error')?.focus();
        }, 100);
        return;
      }
    } catch (err) {
      // OBS-11: the UI degrades and carries on, but the failure is reported (once per page load).
      reportSwallowed('Onboarding.customQuestionCheck', err);
    }

    try {
      await gs.submitIntake?.(packet.id);
      const _res = await gs.finalizeIntake?.(packet.id, {
        id: packet.id,
        inviteId: (packet as any).inviteId,
        clientEmail: (packet as any).clientEmail,
        status: (packet as any).status,
        goals: [],
        trackers: Array.isArray((packet as any).trackers) ? (packet as any).trackers : [],
      });
      try {
        gs?.setUserType?.('client');
        localStorage.setItem('coachd:userType', 'client');
      } catch {
        // OBS-11 a-ignore: role hint for the next landing page only; a storage/setter failure just means the default route.
      }
      setSubmitSuccess(true);
      // Brief success message before redirect
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (e: any) {
      setError(e?.message || 'Submit failed');
    }
  };

  if (loading || resolvingInvite) return <div className="p-6">Loading...</div>;
  if (needsVerification)
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="bg-card border border-border rounded-xl shadow-lg max-w-md w-full p-8 text-center space-y-5">
          <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <MessageSquare className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Verify your email</h2>
            <p className="text-muted-foreground text-sm mt-1">
              We sent a confirmation link to <span className="font-medium text-foreground">{verificationEmail}</span>.
              Please check your inbox and click the link to continue.
            </p>
          </div>

          {verifyError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">
              {verifyError}
            </div>
          )}

          <div className="flex flex-col gap-3 pt-1">
            <button
              disabled={verifyRetrying}
              onClick={async () => {
                setVerifyRetrying(true);
                setVerifyError(null);
                try {
                  // Best effort: re-sync the session, then retry the packet load regardless.
                  await syncSupabaseSession(getSupabase());
                  const res = await fetch(`/api/onboarding/packet?invite=${encodeURIComponent(resolvedInviteId)}`, {
                    credentials: 'include',
                  });
                  // OBS-11 a-ignore: an unparseable body becomes null, which the !ok / missing-field check right after handles.
                  const json = await res.json().catch(() => null);
                  if (res.ok && json?.ok && json?.packet?.id) {
                    setNeedsVerification(false);
                    setPacket(json.packet);
                    if (json.packet.formFlags) setFormFlags(json.packet.formFlags);
                    if (json.packet.trackers) setTrackerDefs(json.packet.trackers);
                    if (json.packet.personal) setPersonal(json.packet.personal);
                    if (json.packet.experience) setExperienceVals(json.packet.experience);
                    if (json.packet.customQuestions) {
                      setCustomDefs(
                        Object.entries(json.packet.customQuestions).map(([key, val]: any) => ({
                          key,
                          prompt: String(val?.label ?? val?.prompt ?? ''),
                          required: !!val?.required,
                        })),
                      );
                    }
                    if (json.packet.customResponses) setCustomAnswers(json.packet.customResponses);
                  } else if (json?.code === 'EMAIL_NOT_VERIFIED') {
                    setVerifyError('Email not yet verified. Please check your inbox and click the confirmation link.');
                  } else {
                    setVerifyError(json?.message || 'Something went wrong. Please try again.');
                  }
                } catch {
                  setVerifyError('Network error. Please try again.');
                } finally {
                  setVerifyRetrying(false);
                }
              }}
              className="w-full px-4 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {verifyRetrying ? 'Checking…' : "I've verified my email"}
            </button>

            <button
              disabled={resendCooldown > 0}
              onClick={async () => {
                try {
                  const { getSupabase } = await import('@/lib/supabase');
                  const sb = getSupabase();
                  if (!sb) return;
                  const { error: resendErr } = await sb.auth.resend({
                    type: 'signup',
                    email: verificationEmail,
                  });
                  if (resendErr) {
                    setVerifyError(resendErr.message);
                    return;
                  }
                  setResendCooldown(60);
                  setVerifyError(null);
                } catch (e: any) {
                  setVerifyError(e?.message || 'Failed to resend');
                }
              }}
              className="w-full px-4 py-2 rounded-lg bg-muted text-muted-foreground text-sm font-medium hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend confirmation email'}
            </button>
          </div>

          <p className="text-muted-foreground text-xs">
            Wrong email?{' '}
            <button
              onClick={async () => {
                await signOutOnServer();
                const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
                window.location.href = `/client/auth?returnTo=${returnTo}`;
              }}
              className="text-primary hover:underline"
            >
              Sign in with a different account
            </button>
          </p>
        </div>
      </div>
    );
  if (error)
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="bg-card border border-border rounded-xl shadow-lg max-w-md w-full p-8 text-center space-y-4">
          <div className="text-red-400 text-lg font-medium">Unable to load onboarding</div>
          <p className="text-muted-foreground text-sm">{error}</p>
          <div className="flex gap-3 justify-center pt-2">
            <button
              onClick={async () => {
                await signOutOnServer();
                const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
                window.location.href = `/client/auth?returnTo=${returnTo}`;
              }}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Sign in with a different account
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-muted text-muted-foreground text-sm font-medium hover:bg-accent transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  if (!packet) {
    return <div className="p-6">No onboarding packet found.</div>;
  }

  // Flags: from packet formFlags (latest-wins)
  // All fields show by default unless explicitly set to false
  const flags = {
    include_full_name: (formFlags as any)?.include_full_name !== false,
    include_age: (formFlags as any)?.include_age !== false,
    include_motivation: (formFlags as any)?.include_motivation !== false,
    include_experience: (formFlags as any)?.include_experience !== false,
    include_injuries: (formFlags as any)?.include_injuries !== false,
    include_trackers: (formFlags as any)?.include_trackers !== false,
    include_height: (formFlags as any)?.include_height !== false,
    include_weight: (formFlags as any)?.include_weight !== false,
  } as const;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header with gradient background */}
        <div className="bg-gradient-to-r from-card to-muted p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-semibold text-foreground">Client Onboarding</h1>
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-muted rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
              aria-label="Close onboarding and go back"
            >
              <X className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-muted-foreground mb-2">
              <span>Progress</span>
              <span>Step 1 of 1</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div className="bg-primary h-2 rounded-full w-full"></div>
            </div>
          </div>

          {/* Single view chip */}
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentView('intake')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${'bg-primary text-primary-foreground'}`}
            >
              Client Intake Form
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-6">
            {/* Personal Information Section */}
            {(flags.include_full_name || flags.include_age || flags.include_height || flags.include_weight) && (
              <div className="bg-muted border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <User className="w-5 h-5 text-muted-foreground" />
                  <h2 className="text-xl font-medium text-foreground">Personal Information</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start content-start">
                  {flags.include_full_name && (
                    <input
                      className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none transition-colors"
                      placeholder="Full name"
                      value={personal?.full_name || ''}
                      onChange={(e) => writeAnswer('personal', 'full_name', e.target.value)}
                    />
                  )}
                  {flags.include_full_name && !String(personal?.full_name || '').trim() && (
                    <div className="col-span-1 md:col-span-2 text-xs text-red-400">Required</div>
                  )}
                  {flags.include_age && (
                    <input
                      className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none transition-colors"
                      placeholder="Age"
                      value={personal?.age || ''}
                      onChange={(e) => writeAnswer('personal', 'age', e.target.value)}
                    />
                  )}
                  {flags.include_age &&
                    !String(personal?.age ?? '')
                      .toString()
                      .trim() && <div className="col-span-1 md:col-span-2 text-xs text-red-400">Required</div>}
                  {flags.include_height && (
                    <div className="flex gap-2">
                      <input
                        className="flex-1 rounded-lg border border-border bg-muted px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none transition-colors"
                        type="number"
                        min={0}
                        step="1"
                        placeholder="Height"
                        value={personal?.height ?? ''}
                        onChange={(e) =>
                          writeAnswer('personal', 'height', e.target.value ? Number(e.target.value) : null)
                        }
                      />
                      <select
                        className="w-20 rounded-lg border border-border bg-muted px-2 py-3 text-foreground focus:border-ring focus:outline-none transition-colors"
                        value={personal?.heightUnit || 'in'}
                        onChange={(e) => writeAnswer('personal', 'heightUnit', e.target.value)}
                      >
                        <option value="in">in</option>
                        <option value="cm">cm</option>
                      </select>
                    </div>
                  )}
                  {flags.include_height &&
                    (personal?.height === undefined ||
                      personal?.height === null ||
                      String(personal?.height) === '') && (
                      <div className="col-span-1 md:col-span-2 text-xs text-red-400">Required</div>
                    )}
                  {flags.include_weight && (
                    <div className="flex gap-2">
                      <input
                        className="flex-1 rounded-lg border border-border bg-muted px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none transition-colors"
                        type="number"
                        min={0}
                        step="0.1"
                        placeholder="Weight"
                        value={personal?.weight ?? ''}
                        onChange={(e) =>
                          writeAnswer('personal', 'weight', e.target.value ? Number(e.target.value) : null)
                        }
                      />
                      <select
                        className="w-20 rounded-lg border border-border bg-muted px-2 py-3 text-foreground focus:border-ring focus:outline-none transition-colors"
                        value={personal?.weightUnit || 'lbs'}
                        onChange={(e) => writeAnswer('personal', 'weightUnit', e.target.value)}
                      >
                        <option value="lbs">lbs</option>
                        <option value="kg">kg</option>
                      </select>
                    </div>
                  )}
                  {flags.include_weight &&
                    (personal?.weight === undefined ||
                      personal?.weight === null ||
                      String(personal?.weight) === '') && (
                      <div className="col-span-1 md:col-span-2 text-xs text-red-400">Required</div>
                    )}
                </div>
              </div>
            )}

            {/* Motivation Section */}
            {flags.include_motivation && (
              <div className="bg-muted border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-5 h-5 text-muted-foreground" />
                  <h2 className="text-xl font-medium text-foreground">What motivates you most?</h2>
                </div>
                <textarea
                  className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none transition-colors resize-none"
                  rows={3}
                  placeholder="Tell us what drives you to achieve your fitness goals..."
                  value={personal?.motivation || ''}
                  onChange={(e) => writeAnswer('personal', 'motivation', e.target.value)}
                />
                {!String(personal?.motivation || '').trim() && (
                  <div className="text-xs text-red-400 mt-1">Required</div>
                )}
              </div>
            )}

            {/* Experience Level Section */}
            {flags.include_experience && (
              <div className="bg-muted border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Activity className="w-5 h-5 text-muted-foreground" />
                  <h2 className="text-xl font-medium text-foreground">Experience Level</h2>
                </div>
                <div className="flex gap-4">
                  {['beginner', 'intermediate', 'advanced'].map((level) => (
                    <label key={level} className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="radio"
                        name="exp"
                        checked={(experienceVals?.level || '') === level}
                        onChange={() => writeAnswer('experience', 'level', level)}
                        className="w-4 h-4 text-foreground bg-muted border-border focus:ring-ring focus:ring-1"
                      />
                      <span className="capitalize text-foreground group-hover:text-foreground transition-colors">
                        {level}
                      </span>
                    </label>
                  ))}
                </div>
                {!String(experienceVals?.level || '').trim() && (
                  <div className="text-xs text-red-400 mt-1">Required</div>
                )}
              </div>
            )}

            {/* Injuries Section */}
            {flags.include_injuries && (
              <div className="bg-muted border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <MessageSquare className="w-5 h-5 text-muted-foreground" />
                  <h2 className="text-xl font-medium text-foreground">Any injury limitations?</h2>
                </div>
                <textarea
                  className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none transition-colors resize-none"
                  rows={3}
                  placeholder="Please describe any injuries or physical limitations we should know about..."
                  value={experienceVals?.injuries || ''}
                  onChange={(e) => writeAnswer('experience', 'injuries', e.target.value)}
                />
                {!String(experienceVals?.injuries || '').trim() && (
                  <div className="text-xs text-red-400 mt-1">Required</div>
                )}
              </div>
            )}

            {/* Trackers Section */}
            {flags.include_trackers && (
              <div className="bg-muted border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                  <h2 className="text-xl font-medium text-foreground">What we'll track</h2>
                </div>
                {/* Read-only simple list of tracker titles */}
                <div className="mt-4 space-y-2">
                  {(trackerDefs || []).map((t: any, i: number) => (
                    <div key={i} className="bg-card border border-border rounded-lg px-4 py-3 text-sm text-foreground">
                      {t.title || 'Tracker'}
                    </div>
                  ))}
                  {(!trackerDefs || trackerDefs.length === 0) && (
                    <div className="text-xs text-muted-foreground">No trackers configured.</div>
                  )}
                </div>
              </div>
            )}

            {/* Custom Questions Section */}
            {customDefs.length > 0 && (
              <div className="bg-muted border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <MessageSquare className="w-5 h-5 text-muted-foreground" />
                  <h2 className="text-xl font-medium text-foreground">Additional Questions</h2>
                </div>
                <div className="space-y-4">
                  {customDefs.map((q) => (
                    <div key={q.key} className="space-y-2">
                      <label className="block text-sm font-medium text-foreground">
                        {q.prompt}
                        <span className="text-foreground ml-1">*</span>
                      </label>
                      <textarea
                        className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none transition-colors resize-none"
                        rows={3}
                        value={customAnswers[q.key] ?? ''}
                        onChange={(e) => saveCustomAnswer(q.key, e.target.value)}
                        onBlur={(e) => saveCustomAnswer(q.key, e.target.value)}
                        placeholder="Type your response..."
                      />
                      {!String(customAnswers[q.key] || '').trim() && (
                        <div className="text-xs text-red-400">Required</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-6 bg-muted">
          {/* Success message */}
          {submitSuccess && (
            <div
              className="mb-4 bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-green-400 text-center"
              role="status"
              aria-live="polite"
            >
              <Check className="w-5 h-5 inline mr-2" aria-hidden="true" />
              Onboarding complete! Redirecting...
            </div>
          )}

          {/* Validation error message */}
          {validationError && (
            <div
              id="validation-error"
              className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400"
              role="alert"
              aria-live="assertive"
              tabIndex={-1}
            >
              {validationError}
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">You can edit these anytime with your coach.</span>
            <button
              onClick={onSubmit}
              disabled={
                submitSuccess ||
                // disable if any required fields missing
                (flags.include_full_name && !String(personal?.full_name || '').trim()) ||
                (flags.include_age && !String(personal?.age ?? '').trim()) ||
                (flags.include_height &&
                  (personal?.height === undefined || personal?.height === null || String(personal?.height) === '')) ||
                (flags.include_weight &&
                  (personal?.weight === undefined || personal?.weight === null || String(personal?.weight) === '')) ||
                (flags.include_motivation && !String(personal?.motivation || '').trim()) ||
                (flags.include_experience && !String(experienceVals?.level || '').trim()) ||
                (flags.include_injuries && !String(experienceVals?.injuries || '').trim()) ||
                customDefs.some((q) => !String(customAnswers[q.key] || '').trim())
              }
              aria-describedby={validationError ? 'validation-error' : undefined}
              className="bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground px-6 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
            >
              Complete Onboarding
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function TrackerAdder({ onAdd }: { onAdd: (t: any) => void }) {
  const [mode, setMode] = React.useState<'create' | 'select'>('create');
  const [title, setTitle] = React.useState('');
  const [unit, setUnit] = React.useState('lbs');
  const [cadence, setCadence] = React.useState<'daily' | 'weekly'>('weekly');
  const [globalTrackers, setGlobalTrackers] = React.useState<any[]>([]);
  const [selectedTrackerId, setSelectedTrackerId] = React.useState('');
  const [loadingTrackers, setLoadingTrackers] = React.useState(false);

  // Load global trackers when switching to select mode
  React.useEffect(() => {
    if (mode === 'select' && globalTrackers.length === 0) {
      loadGlobalTrackers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const loadGlobalTrackers = async () => {
    setLoadingTrackers(true);
    try {
      const gs = (window as any).gsRef;
      if (!gs?.currentCoach?.id) {
        console.warn('No coach ID available for loading global trackers');
        return;
      }

      // Fetch global trackers from API
      const response = await fetch(`/api/coach/trackers/global?coachId=${gs.currentCoach.id}`);
      if (response.ok) {
        const data = await response.json();
        setGlobalTrackers(data.trackers || []);
      }
    } catch (error) {
      console.error('Failed to load global trackers:', error);
      reportSwallowed('Onboarding.loadGlobalTrackers', error);
    } finally {
      setLoadingTrackers(false);
    }
  };

  const handleAdd = () => {
    if (mode === 'create') {
      if (title && unit) {
        onAdd({ title, unit, cadence, reminders: true, showInAnalytics: true });
        setTitle('');
        setUnit('lbs');
      }
    } else {
      // Select mode
      const selected = globalTrackers.find((t) => t.id === selectedTrackerId);
      if (selected) {
        onAdd({
          title: selected.title,
          unit: selected.unit,
          cadence: selected.schedule?.cadence || 'weekly',
          reminders: selected.schedule?.remindersEnabled ?? true,
          showInAnalytics: true,
        });
        setSelectedTrackerId('');
      }
    }
  };

  return (
    <div className="space-y-3">
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <button
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            mode === 'create' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
          }`}
          onClick={() => setMode('create')}
        >
          Create New
        </button>
        <button
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            mode === 'select' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
          }`}
          onClick={() => setMode('select')}
        >
          From Library
        </button>
      </div>

      {/* Create Mode */}
      {mode === 'create' && (
        <div className="flex gap-3">
          <input
            className="flex-1 rounded-lg border border-border bg-muted px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none transition-colors"
            placeholder="Tracker name (e.g., Weight, Steps)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <select
            className="w-28 rounded-lg border border-border bg-muted px-4 py-3 text-foreground focus:border-ring focus:outline-none transition-colors"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          >
            <option value="">Unit</option>
            <option value="lbs">lbs</option>
            <option value="kg">kg</option>
            <option value="%">%</option>
            <option value="reps">reps</option>
            <option value="min">min</option>
            <option value="mi">mi</option>
            <option value="km">km</option>
            <option value="in">in</option>
            <option value="cm">cm</option>
            <option value="cal">cal</option>
            <option value="value">value</option>
          </select>
          <select
            className="w-32 rounded-lg border border-border bg-muted px-4 py-3 text-foreground focus:border-ring focus:outline-none transition-colors"
            value={cadence}
            onChange={(e) => setCadence(e.target.value as any)}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
          <button
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleAdd}
            disabled={!title.trim() || !unit}
          >
            Add Tracker
          </button>
        </div>
      )}

      {/* Select Mode */}
      {mode === 'select' && (
        <div className="flex gap-3">
          {loadingTrackers ? (
            <div className="flex-1 text-center py-3 text-muted-foreground">Loading trackers...</div>
          ) : globalTrackers.length === 0 ? (
            <div className="flex-1 text-center py-3 text-muted-foreground">
              No global trackers found. Create trackers in the Forms & Trackers section first.
            </div>
          ) : (
            <>
              <select
                className="flex-1 rounded-lg border border-border bg-muted px-4 py-3 text-foreground focus:border-ring focus:outline-none transition-colors"
                value={selectedTrackerId}
                onChange={(e) => setSelectedTrackerId(e.target.value)}
              >
                <option value="">Select a tracker from library...</option>
                {globalTrackers.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title} ({t.unit})
                  </option>
                ))}
              </select>
              <button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleAdd}
                disabled={!selectedTrackerId}
              >
                Add Tracker
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function _TrackerList({
  trackers,
  onRemove,
  editingTracker,
  setEditingTracker,
  readOnly,
}: {
  trackers: any[];
  onRemove: (title: string) => void;
  editingTracker: string | null;
  setEditingTracker: (id: string | null) => void;
  readOnly?: boolean;
}) {
  const DOW = [
    { key: 'sunday', label: 'Su' },
    { key: 'monday', label: 'Mo' },
    { key: 'tuesday', label: 'Tu' },
    { key: 'wednesday', label: 'We' },
    { key: 'thursday', label: 'Th' },
    { key: 'friday', label: 'Fr' },
    { key: 'saturday', label: 'Sa' },
  ];

  if (trackers.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No trackers added yet. Add your first tracker above.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {trackers.map((tracker, i) => {
        const isEditing = !readOnly && editingTracker === tracker.id;
        const days = Array.isArray(tracker.days) ? tracker.days : [];

        return (
          <div
            key={i}
            className={`bg-card border rounded-lg p-4 transition-all ${
              isEditing ? 'border-ring bg-primary/5' : 'border-border hover:border-muted-foreground/30'
            }`}
          >
            {/* Row 1: Name, Unit, Frequency, Reminders */}
            <div className="flex items-center gap-3 mb-3">
              <input
                className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  isEditing
                    ? 'border-border bg-muted text-foreground focus:border-ring focus:outline-none'
                    : 'border-transparent bg-muted text-muted-foreground cursor-default'
                }`}
                value={tracker.title || ''}
                readOnly={!isEditing}
                placeholder="Tracker name"
              />
              <input
                className={`w-20 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  isEditing
                    ? 'border-border bg-muted text-foreground focus:border-ring focus:outline-none'
                    : 'border-transparent bg-muted text-muted-foreground cursor-default'
                }`}
                value={tracker.unit || ''}
                readOnly={!isEditing}
                placeholder="Unit"
              />
              <select
                className={`w-24 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  isEditing
                    ? 'border-border bg-muted text-foreground focus:border-ring focus:outline-none'
                    : 'border-transparent bg-muted text-muted-foreground cursor-default pointer-events-none'
                }`}
                value={tracker.cadence || 'weekly'}
                disabled={!isEditing}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={!!tracker.reminders}
                  disabled={!isEditing}
                  className="w-4 h-4 text-green-500 bg-muted border-border rounded focus:ring-green-500 focus:ring-2"
                />
                <span className="text-xs">Reminders</span>
              </label>
            </div>

            {/* Row 2: Days of week */}
            <div className="flex gap-1 mb-3">
              {DOW.map((day) => (
                <button
                  key={day.key}
                  type="button"
                  disabled={!isEditing}
                  className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                    days.includes(day.key)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-accent'
                  } ${!isEditing ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  {day.label}
                </button>
              ))}
            </div>

            {/* Row 3: Analytics checkbox + Action buttons */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={!!tracker.showInAnalytics}
                  disabled={!isEditing}
                  className="w-4 h-4 text-green-500 bg-muted border-border rounded focus:ring-green-500 focus:ring-2"
                />
                <span className="text-xs">Show in Analytics</span>
              </label>

              <div className="flex items-center gap-2">
                {readOnly ? (
                  <span className="text-xs text-muted-foreground">Configured by your coach</span>
                ) : !isEditing ? (
                  <>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Saved
                    </span>
                    <button
                      className="text-xs bg-muted hover:bg-accent text-foreground px-3 py-1 rounded transition-colors"
                      onClick={() => setEditingTracker(tracker.id)}
                    >
                      Edit
                    </button>
                    <button
                      className="text-xs bg-red-600/20 hover:bg-red-600/30 text-red-400 px-3 py-1 rounded transition-colors"
                      onClick={() => onRemove(tracker.title)}
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="text-xs bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1 rounded transition-colors"
                      onClick={() => setEditingTracker(null)}
                    >
                      Save
                    </button>
                    <button
                      className="text-xs bg-muted hover:bg-accent text-foreground px-3 py-1 rounded transition-colors"
                      onClick={() => setEditingTracker(null)}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
